import { useEffect, useState, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Cookies from 'js-cookie'
import styled from 'styled-components'
import { useAuth } from 'context/userAuthContext'
import { confirmEmail } from 'services/emailConfirmation'
import { Stack, Title, Text, Loader, Button, Paper, Space } from 'components'
import { Center } from '@mantine/core'
import { IconCircleCheck, IconAlertCircle, IconArrowRight } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { SchoolLogoColor } from '@legacy/common/types/whitelabel'
import Logo from '@legacy/common/components/shared/Logo'
import DeviseLanguage from '@legacy/common/components/DeviseLanguage/deviseLanguage'
import LumiarLogin from '@legacy/assets/images/lumiar_login.webp'
import { useFeatureToggle } from 'hooks'

/**
 * Clears all browser storage to ensure clean state before authentication.
 * Preserves only essential non-user data like language preferences.
 */
const clearBrowserStorage = () => {
  console.log('[EmailConfirmation] Clearing browser storage...')

  // Preserve language preference
  const preservedLang = localStorage.getItem('i18nextLng')

  // Preserve retry flag to prevent infinite reload loop
  const hasRetried = sessionStorage.getItem('email_confirmation_retried')

  // Clear all storage
  localStorage.clear()
  sessionStorage.clear()

  // Restore preserved data
  if (preservedLang) {
    localStorage.setItem('i18nextLng', preservedLang)
  }
  if (hasRetried) {
    sessionStorage.setItem('email_confirmation_retried', hasRetried)
  }

  // Clear cookies except redirect URL (needed for post-confirmation redirect)
  const allCookies = Cookies.get()
  const redirectUrl = allCookies['url_to_redirect']
  Object.keys(allCookies).forEach(name => Cookies.remove(name))
  if (redirectUrl) {
    Cookies.set('url_to_redirect', redirectUrl)
  }

  console.log('[EmailConfirmation] ✅ Browser storage cleared')
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2.4rem;
  background-image: url('${LumiarLogin}');
  background-size: cover;
  background-position: bottom center;
  height: 16rem;
  @media (max-width: 425px) { flex-direction: column; align-items: center; }
`

// Cast keeps Paper's polymorphic props (shadow/radius/p) visible through styled()
const Card = styled(Paper)`
  width: 34rem;
  @media (max-width: 800px) { width: calc(100vw - 40px); }
` as unknown as typeof Paper

/**
 * Email Confirmation Page
 *
 * Handles email confirmation after user clicks link in email.
 * Calls backend to confirm email and create authenticated session.
 *
 * NOTE: You may see a 401 error for /api/users/current in console.
 * This is EXPECTED behavior - it happens because other parts of the app
 * try to fetch user data before authentication completes. The error is
 * harmless and doesn't affect the confirmation flow.
 */
export const EmailConfirmation = () => {
  const [searchParams] = useSearchParams()
  const { login } = useAuth()
  const { t, i18n } = useTranslation()
  const arcNewProjectEnabled = useFeatureToggle('arc-mosaico-project')

  // Arc: forçar idioma inglês
  useEffect(() => {
    if (arcNewProjectEnabled) {
      i18n.changeLanguage('en')
    }
  }, [arcNewProjectEnabled, i18n])

  // Prevent double call (React StrictMode in dev)
  const hasCalledRef = useRef(false)
  const loginRef = useRef(login)

  // Keep loginRef updated
  useEffect(() => {
    loginRef.current = login
  }, [login])

  const [state, setState] = useState<{
    loading: boolean
    error: string | null
    success: boolean
    alreadyConfirmed: boolean
  }>({
    loading: true,
    error: null,
    success: false,
    alreadyConfirmed: false,
  })

  useEffect(() => {
    const confirmationToken = searchParams.get('confirmation_token')

    // Prevent double execution
    if (hasCalledRef.current) {
      console.log('[EmailConfirmation] Already called, skipping...')
      return
    }

    // Mark as called FIRST to prevent any race conditions
    hasCalledRef.current = true

    // Clear storage before any operation to avoid conflicts with previous user data
    clearBrowserStorage()

    if (!confirmationToken) {
      setState({
        loading: false,
        error: t('email_confirmation.token_not_found'),
        success: false,
        alreadyConfirmed: false,
      })
      return
    }

    console.log('[EmailConfirmation] Starting email confirmation...')

    // Call backend to confirm email and create session
    confirmEmail(confirmationToken)
      .then(async (result) => {
        console.log('[EmailConfirmation] Promise resolved, result:', result)

        if (result.success && result.data) {
          console.log('[EmailConfirmation] ✅ Email confirmed successfully!')
          console.log('[EmailConfirmation] User data:', result.data.user)

          // Map backend response to frontend user format (same as login)
          const userData = {
            id: result.data.user.id,
            email: result.data.user.email,
            firstName: result.data.user.first_name,
            lastName: result.data.user.last_name,
            locale: result.data.user.locale || 'pt-BR',
            selectedSchoolId: result.data.user.selected_school_id || '',
            username: result.data.user.username || result.data.user.email,
            uid: result.data.user.id,
          }

          console.log('[EmailConfirmation] Logging in user...')
          // Update auth context (saves to localStorage)
          loginRef.current(userData)

          // Clear retry flag on success
          sessionStorage.removeItem('email_confirmation_retried')

          console.log('[EmailConfirmation] Setting success state...')
          setState({
            loading: false,
            error: null,
            success: true,
            alreadyConfirmed: false,
          })
          console.log('[EmailConfirmation] State updated to success!')

          // Redirect to url_to_redirect cookie or home (same as login)
          console.log('[EmailConfirmation] Redirecting...')
          let urlToRedirect = Cookies.get('url_to_redirect')
          console.log('[EmailConfirmation] Cookie url_to_redirect:', urlToRedirect)

          // Ignore if redirect URL is signin/signup/confirm page
          if (urlToRedirect && (
            urlToRedirect.includes('/signin') ||
            urlToRedirect.includes('/signup') ||
            urlToRedirect.includes('/confirm') ||
            urlToRedirect.includes('account/signin') ||
            urlToRedirect.includes('account/signup') ||
            urlToRedirect.includes('account/confirm')
          )) {
            console.log('[EmailConfirmation] Ignoring auth-related redirect URL')
            urlToRedirect = undefined
          }

          console.log('[EmailConfirmation] Final URL to redirect:', urlToRedirect || '/')

          // Clear the redirect cookie
          Cookies.remove('url_to_redirect')

          setTimeout(() => {
            console.log('[EmailConfirmation] Executing redirect now...')
            window.location.href = urlToRedirect || '/'
          }, 2000)
        } else {
          console.error('[EmailConfirmation] ❌ Confirmation failed:', result.error)

          const errorMessage = result.error?.error || 'Falha ao confirmar email'
          const isAlreadyConfirmed = errorMessage.toLowerCase().includes('already confirmed')

          // Retry once on error (use sessionStorage to prevent infinite loop)
          const hasRetried = sessionStorage.getItem('email_confirmation_retried')
          if (!isAlreadyConfirmed && !hasRetried) {
            console.log('[EmailConfirmation] Retrying with page reload...')
            sessionStorage.setItem('email_confirmation_retried', 'true')
            window.location.reload()
            return
          }

          setState({
            loading: false,
            error: isAlreadyConfirmed ? null : errorMessage,
            success: false,
            alreadyConfirmed: isAlreadyConfirmed,
          })
        }
      })
      .catch((error) => {
        console.error('[EmailConfirmation] ❌ Exception during confirmation:', error)

        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
        const isAlreadyConfirmed = errorMessage.toLowerCase().includes('already confirmed')

        // Retry once on error (use sessionStorage to prevent infinite loop)
        const hasRetried = sessionStorage.getItem('email_confirmation_retried')
        if (!isAlreadyConfirmed && !hasRetried) {
          console.log('[EmailConfirmation] Retrying with page reload...')
          sessionStorage.setItem('email_confirmation_retried', 'true')
          window.location.reload()
          return
        }

        setState({
          loading: false,
          error: isAlreadyConfirmed ? null : errorMessage,
          success: false,
          alreadyConfirmed: isAlreadyConfirmed,
        })
      })
  }, [searchParams, t])

  const getTitle = () => {
    if (state.loading) return t('email_confirmation.confirming_title')
    if (state.success) return t('email_confirmation.success_title')
    if (state.alreadyConfirmed) return t('email_confirmation.already_confirmed_title')
    return t('email_confirmation.error_title')
  }

  return (
    <>
      <Header>
        <Logo color={SchoolLogoColor.White} />
        {!arcNewProjectEnabled && <DeviseLanguage />}
      </Header>
      <Space h='xl' />
      <Center>
        <Stack align='center' spacing='md'>
          <Title order={1}>{getTitle()}</Title>
          <Card shadow='md' radius='md' p='xl'>
            <Stack align='center' spacing='md'>
              {state.loading && (
                <>
                  <Loader size='xl' color='teal' />
                  <Text color='dimmed'>{t('email_confirmation.confirming_message')}</Text>
                </>
              )}
              {!state.loading && state.success && (
                <>
                  <IconCircleCheck size={64} color='#20c997' stroke={1.5} />
                  <Text color='dimmed'>{t('email_confirmation.success_message')}</Text>
                  <Loader size='sm' color='teal' />
                </>
              )}
              {!state.loading && state.alreadyConfirmed && (
                <>
                  <IconCircleCheck size={64} color='#20c997' stroke={1.5} />
                  <Text color='dimmed' size='sm'>{t('email_confirmation.already_confirmed_message')}</Text>
                  <Button component='a' href='/' color='teal' size='md' rightIcon={<IconArrowRight size={18} />} fullWidth mt='md'>
                    {t('email_confirmation.go_to_dashboard')}
                  </Button>
                </>
              )}
              {!state.loading && !state.success && !state.alreadyConfirmed && state.error && (
                <>
                  <IconAlertCircle size={64} color='#fa5252' stroke={1.5} />
                  <Text color='dimmed' size='sm'>{state.error}</Text>
                  <Text color='dimmed' size='xs'>{t('email_confirmation.error_hint')}</Text>
                  <Button
                    component={Link}
                    to='/account/signin'
                    variant='outline'
                    color='gray'
                    size='md'
                    fullWidth
                    mt='md'
                  >
                    {t('email_confirmation.back_to_login')}
                  </Button>
                </>
              )}
            </Stack>
          </Card>
        </Stack>
      </Center>
    </>
  )
}
